import Alert, { AlertVariant } from "@galaxy-io/dls/feedback/Alert";
import Field from "@galaxy-io/dls/inputs/Field";
import Text from "@galaxy-io/dls/text/Text";
import { ToastVariant } from "@galaxy-io/dls/toast/Toast";
import { useToast } from "@galaxy-io/dls/toast/useToast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useRouter } from "@/app/navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Inset } from "@/components/workspace/people-styles";
import { SelectField } from "@/components/workspace/select-field";
import { SurfaceDialog } from "@/components/workspace/surface-dialog";
import { useDialogReset } from "@/components/workspace/use-dialog-reset";
import { apiClient, unwrap } from "@/lib/api-client";
import { toastDefaults } from "@/lib/toast";

export function PersonEnrichmentDialog({
  open,
  username: initialUsername = "",
  personId,
  onClose,
  onStarted,
}: {
  open: boolean;
  username?: string;
  personId?: string;
  onClose: () => void;
  onStarted?: (workflowId: string) => void;
}) {
  const [username, setUsername] = useState(initialUsername);
  const [selected, setSelected] = useState("");
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const client = useQueryClient();
  const router = useRouter();
  const { toast } = useToast();
  const connections = useQuery({
    queryKey: ["github-enrichment-connections"],
    enabled: open,
    queryFn: () => unwrap(apiClient().GET("/workflows/github-connections")),
  });
  const integrationId = selected || connections.data?.[0]?.id || "";
  const start = useMutation({
    mutationFn: () =>
      unwrap(
        apiClient().POST("/workflows/github-person", {
          body: { username, integrationId, requestId, ...(personId ? { personId } : {}) },
        }),
      ),
    onSuccess: (result) => {
      void client.invalidateQueries();
      toast({
        variant: ToastVariant.SUCCESS,
        header: "GitHub Email Enrich started",
        description: `@${username}`,

        action: {
          label: "View run",
          onClick: () =>
            router.push(`/workflows?view=history&runId=${encodeURIComponent(result.workflowId)}`),
        },
        ...toastDefaults(ToastVariant.SUCCESS),
      });
      onStarted?.(result.workflowId);
      onClose();
    },
  });
  useDialogReset(open, () => {
    setUsername(initialUsername);
    setSelected("");
    setRequestId(crypto.randomUUID());
    start.reset();
  });
  return (
    <SurfaceDialog
      open={open}
      title="GitHub Email Enrich"
      onClose={onClose}
      busy={start.isPending}
      footer={
        <Button
          label="Run enrichment"
          isLoading={start.isPending}
          isDisabled={
            !username.trim() ||
            !integrationId ||
            start.isPending ||
            (!!personId && !initialUsername)
          }
          onClick={() => start.mutate()}
        />
      }
    >
      <Inset>
        <Field label="GitHub username">
          <Input
            aria-label="GitHub username"
            value={username}
            readOnly={!!personId}
            onChange={(event) => setUsername(event)}
          />
        </Field>
        {personId && !initialUsername && (
          <Text>This person needs a linked GitHub username to use GitHub Email Enrich.</Text>
        )}
        <SelectField
          label="GitHub connection"
          value={integrationId}
          onChange={setSelected}
          options={(connections.data ?? []).map((connection) => ({
            value: connection.id,
            label: connection.name,
          }))}
          disabled={start.isPending}
        />
        {!connections.isPending && !connections.data?.length && (
          <Text>Add an authenticated GitHub connection in Integrations first.</Text>
        )}
        <Text>
          Refresh this person's profile and search public commits for additional emails. Your chosen
          primary email is preserved.
        </Text>
        {(start.error || connections.error) && (
          <Alert variant={AlertVariant.ERROR}>{(start.error || connections.error)?.message}</Alert>
        )}
      </Inset>
    </SurfaceDialog>
  );
}
